import React, { useState, useEffect, memo, useCallback } from 'react';
import { format } from 'date-fns';

const FacNotifTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const fetchFacData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/facture/getFacToDo');

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();

      setData(result);

    } catch (err) {
      console.error('Erreur lors du chargement des données factures:', err);
      setError(err.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFacData();
  }, [fetchFacData]);

  const calculateTooltipPosition = useCallback((mouseX, mouseY) => {
    const tooltipWidth = 200;
    const tooltipHeight = 80;
    const offset = 10;
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let x = mouseX + offset;
    let y = mouseY + offset;
    
    if (x + tooltipWidth > viewportWidth) {
      x = mouseX - tooltipWidth - offset;
    }
    
    if (y + tooltipHeight > viewportHeight) {
      y = mouseY - tooltipHeight - offset;
    }
    
    if (x < 0) {
      x = offset;
    }
    
    if (y < 0) {
      y = offset;
    }
    
    return { x, y };
  }, []);

  const handleMouseMove = useCallback((e) => {
    const position = calculateTooltipPosition(e.clientX, e.clientY);
    setTooltipPosition(position);
  }, [calculateTooltipPosition]);

  const renderFacItem = useCallback((item, index) => (
    <div
      key={item.id || index}
      className={`fac-item ${hoveredRow === index ? 'hovered' : ''}`}
      onMouseEnter={() => setHoveredRow(index)}
      onMouseLeave={() => setHoveredRow(null)}
      onMouseMove={handleMouseMove}
    >
      <span className="fac-dossier" style={!item.ndossier ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {item.ndossier || 'Indisponible'}
      </span>
      <span className="fac-separator"> - </span>
      <span className="fac-libelle" style={!item.fac ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {item?.fac || 'Indisponible'}
      </span>
      <span className="fac-separator"> - </span>
      <span className="fac-date" style={!item.date ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {item.date ? format(new Date(item.date), 'dd/MM/yyyy') : 'Indisponible'}
      </span>
    </div>
  ), [hoveredRow, handleMouseMove]);

  if (loading) {
    return (
      <div className="fac-notif-table loading">
        <div className="loading-spinner">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fac-notif-table error">
        <div className="error-message">
          Erreur: {error}
        </div>
        <button
          onClick={fetchFacData}
          className="retry-button"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="fac-notif-table empty">
        <div className="empty-message">Aucune facture à établir pour le moment.</div>
      </div>
    );
  }

  return (
    <div className="fac-notif-table">
      <div className="fac-notif-header">
        <span>Facture{data.length > 1 && 's'} à établir ({data.length})</span>
      </div>
      <div className="fac-notif-list">
        {data.map(renderFacItem)}
      </div>
      {hoveredRow !== null && (
        <div
          className="fac-notif-tooltip"
          style={{ 
            left: tooltipPosition.x, 
            top: tooltipPosition.y,
            position: 'fixed'
          }}
        >
          <p><b>Dossier:</b> <span style={!data[hoveredRow]?.ndossier ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow]?.ndossier || 'Indisponible'}</span></p>
          <p><b>Facture:</b> <span style={!data[hoveredRow]?.fac ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow]?.fac ? data[hoveredRow]?.fac.includes('AR') ? `Avenant réalisation ${data[hoveredRow]?.fac}` : data[hoveredRow]?.fac.includes('R') ? `Réalisation ${data[hoveredRow]?.fac}` : `Conception ${data[hoveredRow]?.fac}` : 'Indisponible'}</span></p>
          <p><b>Date:</b> <span style={!data[hoveredRow]?.date ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{format(new Date(data[hoveredRow]?.date), 'dd/MM/yyyy') || 'Indisponible'}</span></p>
        </div>
      )}
    </div>
  );
};

export default memo(FacNotifTable);